import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

const PRESET_COLORS: string[] = [
  // Row 1 - Basic colors
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
  // Row 2 - Reds
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
  // Row 3 - Pastels
  '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
  // Row 4 - Light
  '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd',
  // Row 5 - Medium
  '#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0',
  // Row 6 - Dark
  '#a61c00', '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3c78d8', '#3d85c6', '#674ea7', '#a64d79',
  // Row 7 - Darker
  '#85200c', '#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#1155cc', '#0b5394', '#351c75', '#741b47',
  // Row 8 - Darkest
  '#5b0f00', '#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#1c4587', '#073763', '#20124d', '#4c1130',
];

export interface ColorPickerProps {
  open: boolean;
  currentColor?: string;
  type?: 'fore' | 'back';
  theme?: string;
  customTitle?: string;
  showRemove?: boolean | null;
  onSelect: (color: string) => void;
  onClose: () => void;
}

export function ColorPicker({
  open,
  currentColor = '#000000',
  type = 'fore',
  theme: themeProp,
  customTitle = '',
  showRemove = null,
  onSelect,
  onClose,
}: ColorPickerProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [selectedColor, setSelectedColor] = useState('#000000');
  const [customColor, setCustomColor] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  // watch: open
  useEffect(() => {
    if (open) {
      setSelectedColor(currentColor || (type === 'fore' ? '#000000' : 'transparent'));
      setCustomColor(currentColor || '');
    }
  }, [open, currentColor, type]);

  // computed: dialogClasses
  const dialogClasses = useMemo(() => {
    const classes = ['kstc-dialog', 'kst-dialog', 'kst-color-picker'];
    if (theme) {
      classes.push(theme);
    }
    return classes.join(' ');
  }, [theme]);

  // computed: title
  const title = useMemo(() => {
    if (customTitle) {
      return customTitle;
    }
    return type === 'fore' ? 'Text Color' : 'Background Color';
  }, [customTitle, type]);

  // computed: shouldShowRemove
  const shouldShowRemove = useMemo(() => {
    if (showRemove !== null) {
      return showRemove;
    }
    return type === 'back';
  }, [showRemove, type]);

  // computed: colorRows
  const colorRows = useMemo(() => {
    const rows: string[][] = [];
    for (let i = 0; i < PRESET_COLORS.length; i += 10) {
      rows.push(PRESET_COLORS.slice(i, i + 10));
    }
    return rows;
  }, []);

  // methods
  const selectPresetColor = useCallback((color: string) => {
    setSelectedColor(color);
    setCustomColor(color);
  }, []);

  const handleCustomColorChange = useCallback((value: string) => {
    setCustomColor(value);
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      setSelectedColor(value);
    }
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const applyColor = useCallback(() => {
    onSelect(selectedColor);
    handleClose();
  }, [onSelect, selectedColor, handleClose]);

  const clearColor = useCallback(() => {
    const clearValue = type === 'fore' ? '#000000' : 'transparent';
    onSelect(clearValue);
    handleClose();
  }, [type, onSelect, handleClose]);

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!open) return null;

  return (
    <div
      className={`kstc-backdrop kst-dialog-overlay ${theme}`}
      data-kstc-modal
      onClick={handleBackdropClick}
      onKeyDown={handleKeydown}
    >
      <div
        className={dialogClasses}
        role="dialog"
        aria-labelledby="color-picker-title"
        ref={dialogRef}
      >
        <div className="kstc-header kst-dialog-header">
          <h2 className="kstc-title" id="color-picker-title">
            {title}
          </h2>
          <button
            className="kstc-x kst-dialog-close"
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="kstc-body kst-dialog-body">
          <div className="color-grid">
            {colorRows.map((row, rowIndex) => (
              <div key={rowIndex} className="color-row">
                {row.map((color) => (
                  <button
                    key={color}
                    className={`color-swatch${selectedColor === color ? ' selected' : ''}`}
                    style={{ backgroundColor: color }}
                    title={color}
                    onClick={() => selectPresetColor(color)}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="custom-color-section">
            <label htmlFor="custom-color">Custom Color</label>
            <div className="custom-color-input">
              <input
                id="custom-color"
                type="text"
                value={customColor}
                placeholder="#000000"
                maxLength={7}
                onChange={(e) => handleCustomColorChange(e.target.value)}
              />
              <input
                type="color"
                value={customColor || '#000000'}
                onChange={(e) => handleCustomColorChange(e.target.value)}
                className="color-input-native"
              />
            </div>
          </div>

          <div
            className="color-picker-preview"
            style={{ backgroundColor: selectedColor }}
          />

          {shouldShowRemove && (
            <button className="kst-btn-link clear-color" onClick={clearColor}>
              Remove color
            </button>
          )}
        </div>

        <div className="kstc-actions">
          <button
            className="kstc-btn kstc-btn--secondary"
            onClick={handleClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="kstc-btn kstc-btn--primary"
            onClick={applyColor}
            type="button"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).KstColorPickerReact = ColorPicker;
